import { Injectable } from '@angular/core';
import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, from, map, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { 
  Coupon, 
  CouponUsage, 
  CreateCouponPayload, 
  UpdateCouponPayload, 
  CouponValidationResult 
} from '../../models/coupon.model';

@Injectable({
  providedIn: 'root'
})
export class CouponService {
  private couponCollection: AngularFirestoreCollection<Coupon>;
  private couponUsageCollection: AngularFirestoreCollection<CouponUsage>;

  constructor(private firestore: AngularFirestore) {
    this.couponCollection = this.firestore.collection<Coupon>('coupons');
    this.couponUsageCollection = this.firestore.collection<CouponUsage>('coupon_usage');
  }

  // Admin methods for coupon management
  
  /**
   * Get all coupons with optional status filtering
   */
  getCoupons(status?: 'active' | 'inactive'): Observable<Coupon[]> {
    // Always use simple query first, then filter client-side if needed
    const query = this.firestore.collection<Coupon>('coupons', ref => 
      ref.orderBy('createdDate', 'desc')
    );

    return query.snapshotChanges().pipe(
      map(actions => {
        let coupons = actions.map(a => {
          const data = a.payload.doc.data() as Coupon;
          const id = a.payload.doc.id;
          return { id, ...data };
        });

        // Filter client-side if status is specified
        if (status) {
          coupons = coupons.filter(coupon => coupon.status === status);
        }

        return coupons;
      })
    );
  }

  /**
   * Get coupon by ID
   */
  getCouponById(id: string): Observable<Coupon | undefined> {
    return this.firestore.doc<Coupon>(`coupons/${id}`).snapshotChanges().pipe(
      map(doc => {
        if (doc.payload.exists) {
          const data = doc.payload.data() as Coupon;
          return { id: doc.payload.id, ...data };
        }
        return undefined;
      })
    );
  }

  /**
   * Create a new coupon
   */
  createCoupon(couponData: CreateCouponPayload): Observable<string> {
    const now = new Date().toISOString();
    
    // Clean the data to remove undefined values
    const cleanedData: any = {
      couponName: couponData.couponName,
      applicationType: couponData.applicationType,
      discountPercentage: couponData.discountPercentage,
      status: couponData.status,
      usageCount: 0,
      createdDate: now,
      updatedDate: now
    };

    // Only add optional fields if they have values
    if (couponData.expiryDate !== undefined && couponData.expiryDate !== null && couponData.expiryDate !== '') {
      cleanedData.expiryDate = couponData.expiryDate;
    }
    if ((couponData as any).notes !== undefined && (couponData as any).notes !== null && (couponData as any).notes !== '') {
      cleanedData.notes = (couponData as any).notes;
    }

    return from(this.couponCollection.add(cleanedData)).pipe(
      map(docRef => docRef.id)
    );
  }

  /**
   * Update an existing coupon
   */
  updateCoupon(id: string, couponData: Partial<UpdateCouponPayload>): Observable<void> {
    // Clean the data to remove undefined values
    const updateData: any = {
      updatedDate: new Date().toISOString()
    };

    // Only add fields that have actual values
    Object.keys(couponData).forEach(key => {
      const value = (couponData as any)[key];
      if (value !== undefined && value !== null) {
        // Special handling for different field types
        if (typeof value === 'string' && value !== '') {
          updateData[key] = value;
        } else if (typeof value !== 'string') {
          updateData[key] = value;
        }
      }
    });

    return from(this.firestore.doc(`coupons/${id}`).update(updateData));
  }

  /**
   * Delete a coupon (soft delete by setting status to inactive)
   */
  deleteCoupon(id: string): Observable<void> {
    return from(this.firestore.doc(`coupons/${id}`).update({
      status: 'inactive',
      updatedDate: new Date().toISOString()
    }));
  }

  // Member methods for coupon usage

  /**
   * Check if coupon name already exists in active state
   */
  checkDuplicateCouponName(couponName: string, excludeId?: string): Observable<boolean> {
    let query = this.firestore.collection('coupons').ref
      .where('couponName', '==', couponName)
      .where('status', '==', 'active');

    return from(query.get()).pipe(
      map(snapshot => {
        if (snapshot.empty) return false;
        
        // If we're editing a coupon, exclude it from the duplicate check
        if (excludeId) {
          const duplicates = snapshot.docs.filter(doc => doc.id !== excludeId);
          return duplicates.length > 0;
        }
        
        return true; // Found duplicates
      }),
      catchError((error: any) => {
        console.error('Error checking duplicate coupon name:', error);
        return of(false); // On error, allow creation (conservative approach)
      })
    );
  }

  /**
   * Validate a coupon for use
   */
  validateCoupon(couponName: string, applicationType: 'new_applications' | 'renewal_applications'): Observable<CouponValidationResult> {
    return this.firestore.collection<Coupon>('coupons', ref => 
      ref.where('couponName', '==', couponName)
         .where('status', '==', 'active')
         .where('applicationType', '==', applicationType)
         .limit(1)
    ).get().pipe(
      map(snapshot => {
        if (snapshot.empty) {
          return {
            isValid: false,
            message: 'Invalid coupon.'
          };
        }

        const couponDoc = snapshot.docs[0];
        const coupon = { id: couponDoc.id, ...couponDoc.data() } as Coupon;

        // Check if coupon is expired
        if (coupon.expiryDate) {
          const expiryDate = new Date(coupon.expiryDate);
          const currentDate = new Date();
          if (currentDate > expiryDate) {
            return {
              isValid: false,
              message: 'This coupon has expired.'
            };
          }
        }

        // No usage limits - unlimited usage allowed

        return {
          isValid: true,
          message: 'Coupon is valid.',
          coupon: coupon,
          discountPercentage: coupon.discountPercentage
        };
      })
    );
  }

  /**
   * Realtime validation: watches a coupon by name, status and applicationType.
   * Emits a new CouponValidationResult whenever the coupon becomes valid/invalid or changes.
   */
  watchCouponValidation(couponName: string, applicationType: 'new_applications' | 'renewal_applications'): Observable<CouponValidationResult> {
    const query = this.firestore.collection<Coupon>('coupons', ref =>
      ref.where('couponName', '==', couponName)
         .where('status', '==', 'active')
         .where('applicationType', '==', applicationType)
         .limit(1)
    );

    return query.snapshotChanges().pipe(
      map(actions => {
        if (actions.length === 0) {
          return {
            isValid: false,
            message: 'Invalid coupon.'
          } as CouponValidationResult;
        }

        const payload = actions[0].payload;
        if (!payload.doc.exists) {
          return { isValid: false, message: 'Invalid coupon.' } as CouponValidationResult;
        }

        const coupon = { id: payload.doc.id, ...(payload.doc.data() as Coupon) } as Coupon;

        // Expiry check if present
        if (coupon.expiryDate) {
          const expiryDate = new Date(coupon.expiryDate);
          const currentDate = new Date();
          if (currentDate > expiryDate) {
            return {
              isValid: false,
              message: 'This coupon has expired.'
            } as CouponValidationResult;
          }
        }

        return {
          isValid: true,
          message: 'Coupon is valid.',
          coupon,
          discountPercentage: coupon.discountPercentage
        } as CouponValidationResult;
      })
    );
  }

  /**
   * Apply a coupon (record usage and increment usage count)
   */
  applyCoupon(couponId: string, userId: string, userEmail: string, applicationType: 'new_applications' | 'renewal_applications', orderAmount?: number): Observable<boolean> {
    return this.getCouponById(couponId).pipe(
      map(coupon => {
        if (!coupon) {
          throw new Error('Coupon not found');
        }

        const discountAmount = orderAmount ? (orderAmount * coupon.discountPercentage / 100) : undefined;

        // Record coupon usage
        const usage: CouponUsage = {
          couponId: couponId,
          couponName: coupon.couponName,
          userId: userId,
          userEmail: userEmail,
          usedDate: new Date().toISOString(),
          discountPercentage: coupon.discountPercentage,
          applicationType: applicationType,
          orderAmount: orderAmount,
          discountAmount: discountAmount
        };

        // Add usage record
        this.couponUsageCollection.add(usage);

        // Increment usage count
        this.firestore.doc(`coupons/${couponId}`).update({
          usageCount: coupon.usageCount + 1,
          updatedDate: new Date().toISOString()
        });

        return true;
      })
    );
  }

  /**
   * Get coupon usage history
   */
  getCouponUsageHistory(couponId?: string): Observable<CouponUsage[]> {
    // Use simple query to avoid index requirements
    const query = this.firestore.collection<CouponUsage>('coupon_usage', ref => 
      ref.orderBy('usedDate', 'desc')
    );

    return query.snapshotChanges().pipe(
      map(actions => {
        let usageHistory = actions.map(a => {
          const data = a.payload.doc.data() as any;
          const id = a.payload.doc.id;
          
          // Convert Firestore timestamp to Date for proper handling
          let processedData: CouponUsage = { id, ...data };
          if (data.usedDate && typeof data.usedDate === 'object' && data.usedDate.toDate) {
            // Handle Firestore timestamp
            processedData.usedDate = data.usedDate.toDate().toISOString();
          } else if (data.usedDate && typeof data.usedDate === 'object' && data.usedDate.seconds) {
            // Handle Firestore timestamp in seconds format
            processedData.usedDate = new Date(data.usedDate.seconds * 1000).toISOString();
          }
          
          return processedData;
        });

        // Filter client-side if couponId is specified
        if (couponId) {
          usageHistory = usageHistory.filter(usage => usage.couponId === couponId);
        }

        return usageHistory;
      })
    );
  }

  /**
   * Get user's coupon usage history
   */
  getUserCouponHistory(userId: string): Observable<CouponUsage[]> {
    // Use simple query and filter client-side to avoid index requirements
    return this.firestore.collection<CouponUsage>('coupon_usage', ref => 
      ref.orderBy('usedDate', 'desc')
    ).snapshotChanges().pipe(
      map(actions => {
        let usageHistory = actions.map(a => {
          const data = a.payload.doc.data() as CouponUsage;
          const id = a.payload.doc.id;
          return { id, ...data };
        });

        // Filter client-side for the specific user
        return usageHistory.filter(usage => usage.userId === userId);
      })
    );
  }

  /**
   * Get coupon statistics
   */
  getCouponStats(): Observable<any> {
    return this.getCoupons().pipe(
      map(coupons => {
        const totalCoupons = coupons.length;
        const activeCoupons = coupons.filter(c => c.status === 'active').length;
        const inactiveCoupons = coupons.filter(c => c.status === 'inactive').length;
        const totalUsage = coupons.reduce((sum, c) => sum + c.usageCount, 0);

        return {
          totalCoupons,
          activeCoupons,
          inactiveCoupons,
          totalUsage
        };
      })
    );
  }

  /**
   * Store applied coupon information in the certification document
   */
  storeAppliedCouponInCertification(certificationId: string, coupon: Coupon): Observable<void> {
    const appliedCouponData = {
      appliedCoupon: {
        id: coupon.id,
        couponName: coupon.couponName,
        discountPercentage: coupon.discountPercentage,
        applicationType: coupon.applicationType,
        appliedDate: new Date().toISOString()
      }
    };

    return from(this.firestore.doc(`certifications/${certificationId}`).update(appliedCouponData));
  }

  /**
   * Remove applied coupon information from the certification document
   */
  removeAppliedCouponFromCertification(certificationId: string): Observable<void> {
    return from(this.firestore.doc(`certifications/${certificationId}`).update({
      appliedCoupon: null
    }));
  }
}
